import React, {useState, useMemo} from 'react';
import ColumnDefinition from '../../../pieces/grids/columnGenerator';
import PropTypes from 'prop-types';
import {Box, Typography} from '@mui/material';
import axios from 'axios';
import {toast} from 'react-toastify';
import MyPaginatedGrid from '../../../pieces/grids/paginatedGridWrapper';
import ConfirmModal from '../../../pieces/modals/confirmPopup';
import FormSubmitButtons from '../../../pieces/formSubmitButtons';
import Utils from '../../../utils/utils';
import ApiUtils from '../../../utils/apiUtils';
import base_url from '../../../baseUrls';
import {COLORS} from '../../../styles/styleConstants';
import {DNC_FIELDS} from '../../../constants/allConstants';

export const getAiDoNotCallsGridAdditionalButtons = (viewController) => {
  return [
    {
      key: 2,
      label: 'Refresh Grid',
      variant: 'outlined',
      color: 'primary',
      onClick: () => viewController.refreshGrid(),
    },
  ];
};

function AiDoNotCallsGrid(props) {
  const viewController = props.viewController;
  const ls_name = 'ai_do_not_calls_grid';
  const [components] = useState({});
  const [pendingRemove, setPendingRemove] = useState(null);
  const [removing, setRemoving] = useState(false);

  const closeRemoveConfirm = () => {
    if (removing) return;
    setPendingRemove(null);
  };

  const submitRemove = async () => {
    const target = pendingRemove;
    if (!target?.id) return;
    setRemoving(true);
    try {
      const authToken = await ApiUtils.getAccessToken();
      await axios.post(
        `${base_url.api}ai/remove-do-not-call`,
        {id: target.id},
        {headers: {Authorization: authToken}},
      );
      toast(`${target.phone} removed from the do-not-call list.`, {type: 'success'});
      setPendingRemove(null);
      viewController.refreshGrid();
    } catch (err) {
      toast(err?.response?.data?.message ?? 'Could not remove this number.', {type: 'error'});
    } finally {
      setRemoving(false);
    }
  };

  const getContextMenuItems = (params) => {
    if (!params.node) {
      return [];
    }
    const rowData = params.node.data;
    const selectedCount = params.api?.getSelectedNodes()?.length ?? 0;
    return [
      {
        name: 'Remove from Do Not Call',
        disabled: !rowData?.[DNC_FIELDS.ID.api_name] || selectedCount > 1,
        action: () => setPendingRemove({
          id: rowData[DNC_FIELDS.ID.api_name],
          phone: rowData[DNC_FIELDS.PHONE.api_name],
          name: `${rowData[DNC_FIELDS.FIRST_NAME.api_name] || ''} ${
            rowData[DNC_FIELDS.LAST_NAME.api_name] || ''}`.trim(),
        }),
      },
      'separator',
      {
        name: 'Export Selected (.xlsx)',
        action: () => params.api.exportDataAsExcel({onlySelected: true}),
      },
      'copy',
    ];
  };

  // const modifyDataElement = (el) => {
  //   const idStr = el?.id != null ? String(el.id) : el?._id != null ? String(el._id) : '';
  //   return {
  //     ...el,
  //     id: idStr,
  //     firstName: el?.firstName ?? '',
  //     lastName: el?.lastName ?? '',
  //     phoneNumber: el?.phoneNumber != null ? String(el.phoneNumber) : '',
  //     createdAt: el?.createdAt,
  //     updatedAt: el?.updatedAt,
  //   };
  // };

  const columnDefs = useMemo(() => {
    return [
      new ColumnDefinition({field: DNC_FIELDS.ID, hide: true}),
      new ColumnDefinition({
        field: DNC_FIELDS.PHONE,
        minWidth: 150,
        sortable: true,
        lockPinned: true,
        pinned: 'left',
        checkboxSelection: true,
        width: 200,
      }),
      new ColumnDefinition({
        field: DNC_FIELDS.FIRST_NAME,
        wrapText: true,
        minWidth: 200,
        sortable: true,
      }),
      new ColumnDefinition({field: DNC_FIELDS.LAST_NAME, minWidth: 200, sortable: true, wrapText: true}),
      new ColumnDefinition({
        field: DNC_FIELDS.CREATED_AT,
        minWidth: 160,
        sortable: true,
        valueGetter: ({data}) => {
          if (data) {
            return Utils.formatIsoDateString(data?.[DNC_FIELDS.CREATED_AT.api_name], {});
          }
          return null;
        },
      }),
      new ColumnDefinition({
        field: DNC_FIELDS.UPDATED_AT,
        minWidth: 160,
        sortable: true,
        valueGetter: ({data}) => {
          if (data) {
            return Utils.formatIsoDateString(data?.[DNC_FIELDS.UPDATED_AT.api_name], {});
          }
          return null;
        },
      }),
    ];
  }, []);

  return (
    <Box
      sx={{
        width: '100%',
        pt: 0,
        mt: '-12px',
      }}
    >
      <MyPaginatedGrid
        columnDefs={columnDefs}
        components={components}
        contextMenu={getContextMenuItems}
        ls_name={ls_name}
        registerGrid={viewController.setGridToParent}
        registerTempGrid={viewController.setTempGridToParent}
        fetchParams={props.fetchParams}
        onFetchFail={viewController.onFetchFail}
        pagination={props.pagination}
        refreshTimestamp={viewController.refreshTimestamp}
      />
      {pendingRemove && (
        <ConfirmModal open={true} handleClose={closeRemoveConfirm}>
          <Box sx={{'& .MuiTypography-root': {fontSize: '1rem'}}}>
            <Typography variant={'body1'} mb={1}>
              {'Remove '}
              <Typography component={'span'} fontWeight={600} color={COLORS.globalBlue}>
                {pendingRemove.phone}
              </Typography>
              {`${pendingRemove.name ? ` (${pendingRemove.name})` : ''} from the do-not-call list?`}
            </Typography>
            <Typography variant={'body1'} mb={3} color={COLORS.fontPale}>
              {'AI calls are not switched back on straight away: they resume for the homeowner’s ' +
                'inspection the next time the job is saved with AI calls allowed.'}
            </Typography>
            <FormSubmitButtons
              cancelText={'No'}
              submitText={'Yes'}
              cancelAction={closeRemoveConfirm}
              submitAction={submitRemove}
              disableSubmit={removing}
              disableCancel={removing}
            />
          </Box>
        </ConfirmModal>
      )}
    </Box>
  );
}

AiDoNotCallsGrid.propTypes = {
  pagination: PropTypes.instanceOf(Object).isRequired,
  viewController: PropTypes.instanceOf(Object).isRequired,
  fetchParams: PropTypes.shape({
    url: PropTypes.string.isRequired,
    respKey: PropTypes.string.isRequired,
    options: PropTypes.object,
  }).isRequired,
};

export default AiDoNotCallsGrid;
